/**
 * Read-only renderer for the generated-brief artifact (`brief-generation-widget:v1`)
 * in the Library preview modal. The generated brief is NOT a mountable widget
 * (it's a generation flow, thumbnail-only — see `agents.config.ts` and
 * `BriefGenerationThumbnail`), so it has no registry entry to mount. This
 * renders the brief's persisted snapshot directly from the artifact's
 * `data.brief`: the formatted brief type plus each section in position order,
 * with one read-only TipTap viewer per section.
 *
 * The modal header already shows the artifact name, so this body never renders
 * a title.
 */
import type { BriefGenerationDataType } from '@inference.engine/widgets/web';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { EditorContent } from '@tiptap/react';
import { ClipboardCopyIcon, DownloadIcon, Loader2Icon } from 'lucide-react';
import {
  BRIEF_PROSE_CLASSES,
  BRIEF_SECTION_TITLE_CLASSES,
  downloadBriefMarkdown,
  useBriefSectionViewer,
} from '@/entities/brief';
import { useExportArtifactPdf } from '@/entities/project-artifact';
import { useTranslation } from 'react-i18next';

type BriefSection = BriefGenerationDataType['sections'][number];

interface Props {
  brief: BriefGenerationDataType;
  projectId: string;
  artifactId: string;
}

// Shares BRIEF_PROSE_CLASSES with the live editor so the preview reads
// identical; only the min-height differs (read-only needs no click target).
const PROSE_OVERRIDES = `${BRIEF_PROSE_CLASSES} [&_.ProseMirror]:min-h-[24px]`;

/**
 * Sort sections by ascending `position`. Stable so equal positions preserve
 * the payload order (`Array.prototype.sort` is stable per spec).
 */
function sortByPosition(sections: BriefSection[]): BriefSection[] {
  return [...sections].sort((a, b) => a.position - b.position);
}

/**
 * One section: name plus a read-only TipTap mount of its content. The viewer
 * returns `null` only for structurally-malformed content (not null/empty —
 * those normalize to an empty doc); render the name with an empty body in that
 * case rather than crashing the whole preview.
 */
function BriefArtifactPreviewSection(props: { section: BriefSection }) {
  const { section } = props;
  const editor = useBriefSectionViewer(section.content);

  return (
    <section className="flex flex-col gap-2" data-testid="brief-artifact-preview-section">
      <h2 className={BRIEF_SECTION_TITLE_CLASSES}>{section.name}</h2>
      {editor && <EditorContent editor={editor} className={PROSE_OVERRIDES} />}
    </section>
  );
}

export function BriefArtifactPreview(props: Props) {
  const { t } = useTranslation('projects');

  const { brief } = props;
  const sections = sortByPosition(brief.sections);
  const { exportPdf, isExporting } = useExportArtifactPdf({
    projectId: props.projectId,
    artifactId: props.artifactId,
    artifactTitle: brief.title,
  });

  const handleDownloadPdf = () => {
    if (isExporting) return;
    void exportPdf();
  };

  const handleDownloadMarkdown = () => {
    downloadBriefMarkdown(brief);
  };

  return (
    // Header row (brief type + actions) sits OUTSIDE the scroll container so
    // the Download menu stays always visible while the brief scrolls — a
    // brief is a single document, unlike the per-image hover download in the
    // image preview. Keeping the button in the flow (not floated) means it
    // never overlaps the content's first lines.
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-6 py-4">
        <p
          className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          data-testid="brief-artifact-preview-type"
        >
          {brief.briefType}
        </p>
        {/* Mirrors the BriefDetailHeader actions menu so download affordances
            read the same wherever a brief is shown. */}
        <DropdownMenu>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('projectDetail.briefArtifactPreview.downloadBrief')}
                    data-testid="brief-artifact-actions"
                  >
                    <DownloadIcon className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('projectDetail.briefArtifactPreview.download')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleDownloadPdf}
              disabled={isExporting}
              data-testid="brief-artifact-download-pdf"
            >
              {isExporting ? (
                <Loader2Icon className="mr-2 size-4 animate-spin" aria-hidden />
              ) : (
                <DownloadIcon className="mr-2 size-4" />
              )}
              {isExporting
                ? t('projectDetail.briefArtifactPreview.preparingPdf')
                : t('projectDetail.briefArtifactPreview.downloadPdf')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleDownloadMarkdown}
              data-testid="brief-artifact-download-markdown"
            >
              <ClipboardCopyIcon className="mr-2 size-4" />
              {t('projectDetail.briefArtifactPreview.downloadMarkdown')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6 pt-0">
        {sections.map((section, index) => (
          // Positions can collide (equal positions keep payload order); the
          // list is static read-only chrome, so the index is a stable key.
          <BriefArtifactPreviewSection key={index} section={section} />
        ))}
      </div>
    </div>
  );
}
